import {
  Pill,
  RiArrowRightDownLine,
  RiArrowRightUpLine,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import i18next from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LandTypeNCA, LandTypeNCARow, NaturalCapitalDimension } from '@/api/rest/resources/types/organization';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { CapsuleTrend } from '@/components/Capsule/Capsule';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { GenericTable } from '@/components/widgets/o/GenericTable/GenericTable';
import { SortButton } from '@/components/widgets/o/GenericTable/SortButton';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { useHideTrendFacts } from '@/hooks/useHideTrendFacts';
import { isNullish } from '@/utils/booleans';
import { unitToTonne, valueToTonne } from '@/utils/formatting/formatKgToTon';
import { getTrendTextColor } from '@/utils/getColorForFact';
import { MetadataColumn } from '@/utils/table/mergeColumns';

type TrendCellProps = {
  unitType?: UnitEnum;
  value: number;
  trendDate?: string;
  closingDate?: string;
};

const TrendCell = ({ value, unitType = UnitEnum.none, trendDate, closingDate }: TrendCellProps) => {
  const { t } = useTranslation();

  const getTrendIcon = () => {
    if (value === null || value === undefined) return null;
    if (value === 0) return null;
    if (value > 0) return <RiArrowRightUpLine />;
    return <RiArrowRightDownLine />;
  };

  const trendIcon = getTrendIcon();
  const trendTextColor = getTrendTextColor(value);

  const formattedTrendDate = !isNullish(trendDate)
    ? t('shared.nca.landDistributionDetails.startDate', {
        date: new Date(trendDate).toLocaleDateString(i18next.language),
      })
    : null;

  const formattedClosingDate = !isNullish(closingDate)
    ? t('shared.nca.landDistributionDetails.endDate', {
        date: new Date(closingDate).toLocaleDateString(i18next.language),
      })
    : null;

  const showTooltip = !isNullish(value) && (!isNullish(formattedTrendDate) || !isNullish(formattedClosingDate));
  return (
    <Pill
      size='small'
      className='bg-neutral-black-4'
      style={{ color: trendTextColor }}
      leftAdornment={
        trendIcon && <CapsuleTrend variant={value > 0 ? 'positive' : 'negative'}>{trendIcon}</CapsuleTrend>
      }
    >
      <FactCell value={value} unitType={unitType} tooltip={false} />
      {showTooltip && (
        <Tooltip>
          <TooltipTrigger className='ml-1 items-center'>
            <Icon icon='ri-information-line' className='text-active-54 typography-body1' />
          </TooltipTrigger>
          <TooltipContent>
            {formattedTrendDate}
            <br />
            {formattedClosingDate}
          </TooltipContent>
        </Tooltip>
      )}
    </Pill>
  );
};

type FactCellProps = {
  unitType?: UnitEnum;
  value?: number;
  date?: string;
  tooltip?: boolean;
};

const FactCell = ({ unitType = UnitEnum.none, value, date, tooltip = true }: FactCellProps) => {
  const { t } = useTranslation();

  const formattedValue = useDisplayNumber(value ?? 0);
  const unit = t(`global.ui.unit.${unitType}`);

  let formattedFact = '--';
  if (!isNullish(value)) {
    formattedFact = !isNullish(unit) ? `${formattedValue} ${unit}` : `${formattedValue}`;
  } else {
    return <>{formattedFact}</>;
  }

  const formattedDate = !isNullish(date)
    ? t('shared.nca.landDistributionDetails.referenceDate', {
        date: new Date(date).toLocaleDateString(i18next.language),
      })
    : null;

  const preciseValue = !isNullish(value)
    ? t('global.analysis.preciseValue', {
        value: value.toFixed(4),
      })
    : null;

  const showTooltip = tooltip && (!isNullish(formattedDate) || !isNullish(preciseValue));

  return (
    <>
      {formattedFact}
      {showTooltip && (
        <Tooltip>
          <TooltipTrigger className='ml-1 items-center'>
            <Icon icon='ri-information-line' className='text-active-54 typography-body1' />
          </TooltipTrigger>
          <TooltipContent>
            {formattedDate}
            {formattedDate && preciseValue && <br />}
            {preciseValue}
          </TooltipContent>
        </Tooltip>
      )}
    </>
  );
};

type NaturalCapitalTableProps = Pick<LandTypeNCA, 'land_type' | 'data'> & {
  customColumns?: MetadataColumn<LandTypeNCA['data'][0]>[];
};

const convertNcaRowToTonnes = (row: LandTypeNCARow): LandTypeNCARow => {
  if (row.unit === UnitEnum['kg/ha']) {
    return {
      ...row,
      opening_stock: valueToTonne(row.opening_stock, row.unit) as number,
      closing_stock: valueToTonne(row.closing_stock, row.unit) as number,
      possibility: valueToTonne(row.possibility, row.unit) as number,
      yoy_change: valueToTonne(row.yoy_change, row.unit) as number,
      yoy_change_previous_1y: valueToTonne(row.yoy_change_previous_1y, row.unit) as number,
      unit: unitToTonne(row.unit as UnitEnum),
    };
  }
  return row;
};

export const NaturalCapitalTable = ({ land_type: landType, data, customColumns }: NaturalCapitalTableProps) => {
  const { t } = useTranslation();
  const hideYoyColumn = useHideTrendFacts();

  const formattedData = useMemo(() => {
    // TODO: MVP-7006 undo modifiedData logic
    const soilData = data.find((row) => row.dimension === NaturalCapitalDimension.SOIL);

    const modifiedData = data.map((row) => {
      if (row.dimension === NaturalCapitalDimension.CARBON) {
        return {
          ...row,
          opening_stock_reference_date: !isNullish(soilData?.opening_stock_reference_date)
            ? soilData?.opening_stock_reference_date
            : row.opening_stock_reference_date,
        };
      }
      return row;
    }) as LandTypeNCARow[];

    return modifiedData.map(convertNcaRowToTonnes);
  }, [data]);

  const defaultColumns = useMemo<MetadataColumn<LandTypeNCA['data'][0]>[]>(
    () => [
      {
        accessorKey: 'dimension',
        header: ({ column }) => (
          <SortButton column={column}>{t('shared.nca.landDistributionDetails.tableHeader.dimension')}</SortButton>
        ),
        cell: ({ getValue }) => (
          <span className='typography-overline'>{t(`global.analysis.${getValue() as NaturalCapitalDimension}`)}</span>
        ),
      },
      {
        accessorKey: 'opening_stock',
        header: ({ column }) => (
          <SortButton column={column}>{t('shared.nca.landDistributionDetails.tableHeader.openingStock')}</SortButton>
        ),
        cell: ({ row, getValue }) => {
          const date = row.original.opening_stock_reference_date;
          return <FactCell value={getValue() as number} date={date} />;
        },
      },
      {
        accessorKey: 'closing_stock',
        header: ({ column }) => (
          <SortButton column={column}>{t('shared.nca.landDistributionDetails.tableHeader.closingStock')}</SortButton>
        ),
        cell: ({ row, getValue }) => {
          const date = row.original.closing_stock_reference_date;
          return <FactCell value={getValue() as number} date={date} />;
        },
      },
      ...(!hideYoyColumn
        ? [
            {
              accessorKey: 'yoy_change_previous_1y',
              header: ({ column }) => (
                <SortButton column={column}>{t('shared.nca.landDistributionDetails.tableHeader.yoyChange')}</SortButton>
              ),
              cell: ({ row, getValue }) => {
                const closingDate = row.original.closing_stock_reference_date;
                const trendDate = row.original.yoy_change_previous_1y_reference_date;

                return <TrendCell value={getValue() as number} trendDate={trendDate} closingDate={closingDate} />;
              },
            } as MetadataColumn<LandTypeNCA['data'][0]>,
          ]
        : []),
      {
        accessorKey: 'unit',
        header: ({ column }) => <SortButton column={column}>{t('navbar.units')}</SortButton>,
        cell: ({ getValue }) => <span className='text-secondary'>{getValue() as string}</span>,
      },
      {
        accessorKey: 'possibility',
        header: ({ column }) => (
          <SortButton column={column}>{t('shared.nca.landDistributionDetails.tableHeader.max')}</SortButton>
        ),
        cell: ({ getValue }) => <FactCell value={getValue() as number} />,
      },
    ],
    [t, hideYoyColumn],
  );

  const tooltipContent = {
    dimension: t('shared.nca.landDistributionDetails.tooltips.dimension'),
    opening_stock: t('shared.nca.landDistributionDetails.tooltips.openingStock'),
    yoy_change: t('shared.nca.landDistributionDetails.tooltips.yoyChange'),
    closing_stock: t('shared.nca.landDistributionDetails.tooltips.closingStock'),
    unit: t('shared.nca.landDistributionDetails.tooltips.units'),
    possibility: t('shared.nca.landDistributionDetails.tooltips.max'),
  };

  return (
    <GenericTable
      data={formattedData}
      defaultColumns={defaultColumns}
      customColumns={customColumns}
      title={t(`global.plotTypes.${landType}`)}
      showTooltips={true}
      tooltipContent={tooltipContent}
    />
  );
};
